import { useRef, useId } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINES = ["One Project.", "One Connected Financial System.", "One Point of Control."];

const Medallion = ({ className = "" }) => {
  const uid = useId().replace(/:/g, "");
  const circleId = `sc-${uid}`;
  return (
    <div className={`relative flex items-center justify-center ${className}`}>
      <div className="absolute inset-[18%] rounded-full bg-gold/10 blur-[70px]" aria-hidden="true" />
      <motion.svg
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 200 200"
        aria-hidden="true"
      >
        <defs>
          <path id={circleId} d="M100,100 m-88,0 a88,88 0 1,1 176,0 a88,88 0 1,1 -176,0" fill="none" />
          <linearGradient id={`silver-band-${uid}`} x1="0" y1="0" x2="200" y2="200" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#F5F7FA" />
            <stop offset="0.22" stopColor="#C9CFD8" />
            <stop offset="0.45" stopColor="#8B929E" />
            <stop offset="0.62" stopColor="#E4E8EE" />
            <stop offset="0.82" stopColor="#A6ADB8" />
            <stop offset="1" stopColor="#DDE2E9" />
          </linearGradient>
          <linearGradient id={`silver-edge-${uid}`} x1="0" y1="0" x2="0" y2="200" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FFFFFF" />
            <stop offset="0.5" stopColor="#C0C6CF" />
            <stop offset="1" stopColor="#F0F3F7" />
          </linearGradient>
        </defs>
        <circle cx="100" cy="100" r="88" fill="none" stroke={`url(#silver-band-${uid})`} strokeWidth="17" />
        <circle cx="100" cy="100" r="96.5" fill="none" stroke={`url(#silver-edge-${uid})`} strokeWidth="1.6" />
        <circle cx="100" cy="100" r="79.5" fill="none" stroke="#6E7580" strokeOpacity="0.85" strokeWidth="1.2" />
        <motion.g
          animate={{ rotate: -360 }}
          transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "100px 100px" }}
        >
          <circle cx="100" cy="100" r="88" fill="none" stroke="#FFFFFF" strokeOpacity="0.85" strokeWidth="13" strokeDasharray="13 540" strokeLinecap="round" style={{ filter: "blur(1.8px)" }} />
          <circle cx="100" cy="100" r="88" fill="none" stroke="#FFFFFF" strokeOpacity="0.5" strokeWidth="13" strokeDasharray="6 547" strokeDashoffset="276" strokeLinecap="round" style={{ filter: "blur(1.5px)" }} />
        </motion.g>
        <text dominantBaseline="central" textLength="553" lengthAdjust="spacing" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: "8.8px", letterSpacing: "1.5px", fill: "#2E5FBE" }}>
          <textPath href={`#${circleId}`}>VERIVISTA ADVISORY LLP · TRANSPARENCY IN NUMBERS · TRUST IN EVERY FRAME <tspan fill="#C5A059" style={{ fontSize: "12.5px" }}>✦ ✦ ✦ ✦</tspan></textPath>
        </text>
      </motion.svg>
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 46, repeat: Infinity, ease: "linear" }}
        className="absolute inset-[13%] rounded-full border border-dashed border-gold/35"
        aria-hidden="true"
      />
      <motion.div
        animate={{
          y: [0, -10, 0],
          rotateY: [0, 0, 16, -11, 0, 0],
        }}
        transition={{
          y: { duration: 5.5, repeat: Infinity, ease: "easeInOut" },
          rotateY: { duration: 9, repeat: Infinity, ease: "easeInOut", times: [0, 0.72, 0.8, 0.88, 0.95, 1] },
        }}
        className="relative flex h-[74%] w-[74%] items-center justify-center rounded-full"
        style={{ boxShadow: "0 0 60px rgba(158,43,37,0.4)", transformStyle: "preserve-3d" }}
      >
        <img
          src="/assets/verivista-v-mark.png"
          alt="VERIVISTA Advisory LLP V mark"
          className="h-full w-full rounded-full object-cover"
        />
        <div
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            boxShadow:
              "inset 0 4px 8px rgba(255,255,255,0.55), inset 0 -6px 12px rgba(110,18,12,0.4), inset 3px 0 8px rgba(255,255,255,0.22), inset -4px 0 10px rgba(110,18,12,0.3)",
          }}
          aria-hidden="true"
        />
        <motion.div
          className="pointer-events-none absolute inset-0 rounded-full"
          animate={{ backgroundPosition: ["0% 0%", "220% 0%"] }}
          transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
          style={{
            background: "linear-gradient(115deg, transparent 44%, rgba(197,160,89,0.18) 50%, transparent 56%)",
            backgroundSize: "220% 100%",
          }}
          aria-hidden="true"
        />
      </motion.div>
    </div>
  );
};

const MaskedLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 18 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="hero" ref={ref} className="relative flex min-h-screen items-center overflow-hidden pt-[76px]">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-gold/[0.06] blur-[140px]" />
        <div className="absolute bottom-0 right-0 h-[380px] w-[520px] rounded-full bg-elevated/60 blur-[120px]" />
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-16 px-6 pb-24 pt-16 lg:grid-cols-12 lg:px-10">
        <motion.div style={{ y: textY }} className="flex flex-col justify-center lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8 flex justify-center lg:hidden"
          >
            <Medallion className="aspect-square w-56" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mb-6 font-mono text-[11px] uppercase tracking-[0.38em] text-gold"
          >
            Integrated Commercial, Audit & Accounting Management
          </motion.p>

          <h1 data-testid="hero-title" className="font-serif text-4xl font-medium leading-[1.08] tracking-tight text-ivory sm:text-6xl lg:text-6xl xl:text-7xl">
            {LINES.map((line, i) => (
              <MaskedLine key={line} delay={0.35 + i * 0.16}>
                {i === 2 ? <span className="italic text-gold">{line}</span> : line}
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-mist sm:text-lg"
          >
            From first budget to final cost, we connect the numbers behind Film, OTT, Television,
            Commercial and Media Productions — so producers can focus on what they do best:
            bringing stories to life.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("#contact")}
              className="group flex items-center gap-3 bg-gold px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:bg-gold-bright"
            >
              Request a Consultation
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("#solution")}
              className="border border-white/15 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ivory transition-all duration-300 hover:border-gold/60 hover:text-gold"
            >
              Explore the Model
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="hidden items-center justify-center lg:col-span-5 lg:flex"
          style={{ perspective: 1200 }}
        >
          <motion.div
            data-testid="hero-animated-seal"
            onMouseMove={onTilt}
            onMouseLeave={() => { mx.set(0); my.set(0); }}
            style={{ rotateX, rotateY, y: imgY, transformStyle: "preserve-3d" }}
            className="w-full max-w-[440px]"
          >
            <Medallion className="aspect-square w-full" />
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#challenge")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 text-mist transition-colors hover:text-gold lg:block"
        aria-label="Scroll to content"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="block">
          <ArrowDown size={18} strokeWidth={1.5} />
        </motion.span>
      </motion.button>
    </section>
  );
}
